import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, Lock, ArrowRight, Loader2 } from 'lucide-react';
import './ArtistAuth.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ArtistAuth = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotError, setForgotError] = useState('');
  
  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/artist/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await res.json();

      if (res.ok) {
        localStorage.setItem('artistToken', data.token);
        localStorage.setItem('artistInfo', JSON.stringify(data.artist));
        toast.success('Welcome back!');
        navigate('/artist/dashboard');
      } else {
        toast.error(data.detail || 'Login failed');
      }
    } catch (error) {
      toast.error('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="artist-auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <Link to="/" className="auth-logo">
            <span className="logo-text">AMR</span>
            <span className="logo-subtext">Artist Portal</span>
          </Link>
        </div>

        <div className="auth-card">
          <div className="auth-card-header">
            <h2>Artist Sign In</h2>
            <p>Welcome back to the AMR Artist Portal</p>
          </div>

          <form onSubmit={handleLogin} className="auth-form">
            <div className="form-group">
              <label>
                <Mail size={16} />
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="your@email.com"
                data-testid="login-email"
              />
            </div>

            <div className="form-group">
              <label>
                <Lock size={16} />
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Your password"
                data-testid="login-password"
              />
            </div>

            <button
              type="submit"
              className="auth-submit-btn"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? (
                <Loader2 size={20} className="spinner" />
              ) : (
                <>
                  Sign In
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          {/* Forgot Password */}
          {!forgotMode ? (
            <button
              type="button"
              onClick={() => setForgotMode(true)}
              style={{ display: 'block', width: '100%', background: 'none', border: 'none', color: '#00ffff', marginTop: 12, cursor: 'pointer', fontSize: '0.82rem', textAlign: 'center' }}
              data-testid="forgot-password-link"
            >
              Forgot Password?
            </button>
          ) : forgotSent ? (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <p style={{ color: '#00c864', fontSize: '0.85rem', marginBottom: 8 }}>Reset link sent! Check your inbox.</p>
              <button type="button" onClick={() => { setForgotMode(false); setForgotSent(false); }} style={{ background: 'none', border: 'none', color: '#00ffff', cursor: 'pointer', fontSize: '0.82rem' }}>
                Back to Sign In
              </button>
            </div>
          ) : (
            <div style={{ marginTop: 16, padding: '16px', background: 'rgba(255,255,255,0.03)', borderRadius: 8 }}>
              <p style={{ color: '#ccc', fontSize: '0.82rem', marginBottom: 10 }}>Enter your email to receive a password reset link</p>
              {forgotError && <p style={{ color: '#ff4444', fontSize: '0.78rem', marginBottom: 8 }}>{forgotError}</p>}
              <input
                type="email"
                value={forgotEmail}
                onChange={e => setForgotEmail(e.target.value)}
                placeholder="your@email.com"
                style={{ width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '8px 12px', color: '#fff', fontSize: '0.85rem', marginBottom: 10, boxSizing: 'border-box' }}
                data-testid="forgot-email"
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={() => setForgotMode(false)} style={{ flex: 1, background: 'none', border: '1px solid rgba(255,255,255,0.1)', color: '#888', padding: '8px', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem' }}>
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    if (!forgotEmail) return;
                    setLoading(true); setForgotError('');
                    try {
                      const res = await fetch(`${API_URL}/api/artist/forgot-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: forgotEmail }) });
                      if (res.ok) setForgotSent(true);
                      else setForgotError('Failed to send reset email');
                    } catch { setForgotError('Connection error'); } finally { setLoading(false); }
                  }}
                  disabled={loading}
                  style={{ flex: 1, background: '#00ffff', color: '#000', padding: '8px', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: '0.78rem' }}
                  data-testid="forgot-submit"
                >
                  {loading ? 'Sending...' : 'Send Link'}
                </button>
              </div>
            </div>
          )}

          <div className="auth-info-box">
            <p>Don't have an account?</p>
            <p className="info-note">Artist accounts are created by AMR staff. If you've been invited, check your email for a registration link.</p>
          </div>
        </div>

        <div className="auth-footer">
          <Link to="/">← Back to main site</Link>
          <span className="divider">|</span>
          <Link to="/admin/login">Staff Login</Link>
        </div>
      </div>
    </div>
  );
};

export default ArtistAuth;
